/**
 * Plan of Record service — read/write for the `por_*` domain.
 *
 * Build-contract conventions followed from planningService.ts:
 *  - DB is the source of truth; no localStorage persistence.
 *  - Every function returns `{ data, error }` — never throws to the caller.
 *  - Authority transitions (condition change, ask decision, status change) go
 *    through SECURITY DEFINER RPCs so a governance event is always appended.
 *
 * See docs/PLAN_OF_RECORD_DEVIATIONS.md for how this module relates to canon.
 */

import { getSupabaseClient } from './supabaseClient';
import { getCurrentWorkspaceId } from './supabaseSync';
import { getTenantContext } from './tenantContext';
import type {
  AssemblePorResult,
  CreatePorInput,
  PorCommitment,
  PorCondition,
  PorExecutiveAsk,
  PorKpi,
  PorProofPoint,
  PorRecord,
  PorRisk,
  PorSection,
  PorSignalEvent,
  PorStatus,
  PorAskStatus,
} from '../types/plan-of-record';
import { sortPorsForDisplay } from '../types/plan-of-record';

type Result<T> = { data: T; error: string | null };

function fail<T>(fallback: T, e: unknown): Result<T> {
  return { data: fallback, error: e instanceof Error ? e.message : 'Unexpected error' };
}

// ─── Records ────────────────────────────────────────────────────────────────

/** List every Plan of Record visible in the current workspace (primary first). */
export async function listPors(): Promise<Result<PorRecord[]>> {
  try {
    const supabase = getSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    let query = supabase.from('por_records').select('*').order('updated_at', { ascending: false });
    if (workspaceId) query = query.eq('workspace_id', workspaceId);
    const { data, error } = await query;
    if (error) return { data: [], error: error.message };
    const rows = ((data ?? []) as PorRecord[]).map((r) => ({
      ...r,
      is_primary: Boolean(r.is_primary),
    }));
    return { data: sortPorsForDisplay(rows), error: null };
  } catch (e) {
    return fail<PorRecord[]>([], e);
  }
}

/** Designate one PoR as the workspace primary (clears any previous primary). */
export async function setPrimaryPor(porId: string): Promise<Result<boolean>> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('set_primary_por', { p_por_id: porId });
    if (error) return { data: false, error: error.message };
    const res = data as { success: boolean; error?: string };
    if (!res?.success) return { data: false, error: res?.error ?? 'Failed to set primary Plan of Record.' };
    return { data: true, error: null };
  } catch (e) {
    return fail<boolean>(false, e);
  }
}

/** Create a PoR. Seeds the 10 sections + 6 KPI cards + a governance event. */
export async function createPor(input: CreatePorInput): Promise<Result<{ por_id: string } | null>> {
  try {
    const supabase = getSupabaseClient();
    const workspaceId = getCurrentWorkspaceId() ?? getTenantContext()?.workspaceId ?? null;
    if (!workspaceId) return { data: null, error: 'No active workspace.' };
    if (!input.title.trim()) return { data: null, error: 'Title is required.' };

    const { data, error } = await supabase.rpc('create_por', {
      p_workspace_id: workspaceId,
      p_title: input.title.trim(),
      p_horizon_short: input.horizon_short ?? null,
      p_horizon_long: input.horizon_long ?? null,
      p_owner_id: input.owner_id ?? null,
      p_executive_sponsor_id: input.executive_sponsor_id ?? null,
      p_plan_cycle_id: input.plan_cycle_id ?? null,
    });
    if (error) return { data: null, error: error.message };
    const res = data as { success: boolean; por_id?: string; error?: string };
    if (!res?.success) return { data: null, error: res?.error ?? 'Failed to create Plan of Record.' };
    return { data: { por_id: res.por_id! }, error: null };
  } catch (e) {
    return fail<{ por_id: string } | null>(null, e);
  }
}

/**
 * The narrative record linked to a plan cycle (unified PoR surface, DEP-026).
 * Ties break the same way authorize_por picks its freeze target: primary
 * first, then most recently updated. Null when the cycle has none.
 */
export async function getPorForCycle(cycleId: string): Promise<Result<PorRecord | null>> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('por_records')
      .select('*')
      .eq('plan_cycle_id', cycleId)
      .order('is_primary', { ascending: false })
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) return { data: null, error: error.message };
    return { data: (data as PorRecord) ?? null, error: null };
  } catch (e) {
    return fail<PorRecord | null>(null, e);
  }
}

/**
 * One ceremony per record (migration 678): a cycle-linked narrative is frozen
 * by that plan cycle's authorize_por and can never be ratified independently;
 * only cycle-less org-level records keep the standalone ratify_por ceremony.
 * The RPC enforces this server-side — this predicate is the UI's gate.
 */
export function canRatifyIndependently(
  record: Pick<PorRecord, 'status' | 'plan_cycle_id'>,
): boolean {
  return (
    (record.status === 'draft' || record.status === 'in_review') && record.plan_cycle_id == null
  );
}

/** Fully composed PoR (assemble_por RPC) — the dashboard hydrates from this. */
export async function assemblePor(porId: string): Promise<Result<AssemblePorResult | null>> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('assemble_por', { p_por_id: porId });
    if (error) return { data: null, error: error.message };
    return { data: data as AssemblePorResult, error: null };
  } catch (e) {
    return fail<AssemblePorResult | null>(null, e);
  }
}

/** Patch scalar fields on the PoR record (title, horizons, phase, review date…). */
export async function updatePorRecord(
  porId: string,
  patch: Partial<
    Pick<
      PorRecord,
      | 'title'
      | 'version'
      | 'horizon_short'
      | 'horizon_long'
      | 'current_phase'
      | 'total_phases'
      | 'next_review'
      | 'owner_id'
      | 'executive_sponsor_id'
    >
  >,
): Promise<Result<PorRecord | null>> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('por_records')
      .update({ ...patch, last_updated: new Date().toISOString(), updated_at: new Date().toISOString() })
      .eq('id', porId)
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as PorRecord, error: null };
  } catch (e) {
    return fail<PorRecord | null>(null, e);
  }
}

// ─── Authority transitions (RPC-backed; always log a governance event) ──────

/**
 * May the signed-in person change this PoR's signal condition? Mirrors
 * set_por_condition's gate (migration 735,
 * `internal_rpc_caller_permitted(ws, NULL, true)`): an admin or owner of the
 * PoR's own workspace. `can` answers for the current workspace, so a PoR from
 * another workspace is refused here; the server stays the authority.
 * Admins hold every permission action on the client, so `can('admin.settings')`
 * is the admin test.
 */
export function porConditionSetAllowed(input: {
  can: (action: 'admin.settings') => boolean;
  porWorkspaceId: string | null | undefined;
  currentWorkspaceId: string | null | undefined;
}): boolean {
  if (!input.porWorkspaceId || input.porWorkspaceId !== input.currentWorkspaceId) return false;
  return input.can('admin.settings');
}

export const POR_CONDITION_SET_LINE =
  "Reclassifying the signal condition takes an admin or owner of this plan's workspace.";

/** Change the F2 signal condition. Rationale is required and cannot be empty. */
export async function setPorCondition(
  porId: string,
  condition: PorCondition,
  rationale: string,
  linkedSignalEventId?: string | null,
): Promise<Result<boolean>> {
  try {
    if (!rationale.trim()) return { data: false, error: 'A rationale is required to change the signal condition.' };
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('set_por_condition', {
      p_por_id: porId,
      p_new_condition: condition,
      p_rationale: rationale.trim(),
      p_linked_signal_event_id: linkedSignalEventId ?? null,
    });
    if (error) return { data: false, error: error.message };
    const res = data as { success: boolean; error?: string };
    return { data: !!res?.success, error: res?.success ? null : res?.error ?? 'Failed to change condition.' };
  } catch (e) {
    return fail<boolean>(false, e);
  }
}

/** Move the PoR through its lifecycle (draft → in_review → approved → superseded). */
export async function setPorStatus(
  porId: string,
  status: PorStatus,
  rationale: string,
): Promise<Result<boolean>> {
  try {
    if (!rationale.trim()) return { data: false, error: 'A rationale is required to change PoR status.' };
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('set_por_status', {
      p_por_id: porId,
      p_new_status: status,
      p_rationale: rationale.trim(),
    });
    if (error) return { data: false, error: error.message };
    const res = data as { success: boolean; error?: string };
    return { data: !!res?.success, error: res?.success ? null : res?.error ?? 'Failed to change status.' };
  } catch (e) {
    return fail<boolean>(false, e);
  }
}

// ─── Official record (migration 648) ────────────────────────────────────────

export interface PorRecordSnapshot {
  id: string;
  por_id: string;
  version: number;
  payload: AssemblePorResult & {
    ratified_priorities?: Record<string, unknown>;
    committed_plan?: Record<string, unknown>;
  };
  source_cycle_report_id: string | null;
  source_plan_version_id: string | null;
  ratification_authority: string;
  rationale: string;
  authorized_by: string | null;
  authorized_at: string;
  superseded_by_snapshot_id: string | null;
  created_at: string;
}

/**
 * Ratify a Plan of Record: freezes content, writes the immutable versioned
 * snapshot (optionally embedding the ratified cycle report and/or committed
 * plan version as provenance), supersedes the prior snapshot, and appends the
 * governance event — one transaction (ratify_por, §2.3a).
 */
export async function ratifyPor(
  porId: string,
  opts: {
    authority: string;
    rationale: string;
    sourceCycleReportId?: string | null;
    sourcePlanVersionId?: string | null;
  },
): Promise<Result<{ snapshot_id: string; version: number } | null>> {
  try {
    if (!opts.authority.trim()) return { data: null, error: 'Ratification authority is required.' };
    if (!opts.rationale.trim()) return { data: null, error: 'A rationale is required to ratify.' };
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('ratify_por', {
      p_por_id: porId,
      p_authority: opts.authority.trim(),
      p_rationale: opts.rationale.trim(),
      p_source_cycle_report_id: opts.sourceCycleReportId ?? null,
      p_source_plan_version_id: opts.sourcePlanVersionId ?? null,
    });
    if (error) return { data: null, error: error.message };
    const res = data as { success: boolean; error?: string; snapshot_id?: string; version?: number };
    if (!res?.success) return { data: null, error: res?.error ?? 'Failed to ratify.' };
    return { data: { snapshot_id: res.snapshot_id!, version: res.version! }, error: null };
  } catch (e) {
    return fail<{ snapshot_id: string; version: number } | null>(null, e);
  }
}

/**
 * Reopen a ratified PoR for amendment (approved → in_review). The latest
 * snapshot remains the official record until re-ratification supersedes it.
 */
export async function reopenPorForAmendment(
  porId: string,
  rationale: string,
): Promise<Result<boolean>> {
  try {
    if (!rationale.trim()) return { data: false, error: 'A rationale is required to reopen.' };
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('reopen_por_for_amendment', {
      p_por_id: porId,
      p_rationale: rationale.trim(),
    });
    if (error) return { data: false, error: error.message };
    const res = data as { success: boolean; error?: string };
    return { data: !!res?.success, error: res?.success ? null : res?.error ?? 'Failed to reopen.' };
  } catch (e) {
    return fail<boolean>(false, e);
  }
}

/** List a PoR's official-record snapshots, newest first (metadata only). */
export async function listPorSnapshots(
  porId: string,
): Promise<Result<Omit<PorRecordSnapshot, 'payload'>[]>> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('por_record_snapshots')
      .select(
        'id, por_id, version, source_cycle_report_id, source_plan_version_id, ratification_authority, rationale, authorized_by, authorized_at, superseded_by_snapshot_id, created_at',
      )
      .eq('por_id', porId)
      .order('version', { ascending: false });
    if (error) return { data: [], error: error.message };
    return { data: (data ?? []) as Omit<PorRecordSnapshot, 'payload'>[], error: null };
  } catch (e) {
    return fail<Omit<PorRecordSnapshot, 'payload'>[]>([], e);
  }
}

/** Fetch one official-record snapshot (latest when version omitted). */
export async function getPorSnapshot(
  porId: string,
  version?: number,
): Promise<Result<PorRecordSnapshot | null>> {
  try {
    const supabase = getSupabaseClient();
    let query = supabase.from('por_record_snapshots').select('*').eq('por_id', porId);
    query = version != null ? query.eq('version', version) : query.order('version', { ascending: false }).limit(1);
    const { data, error } = await query.maybeSingle();
    if (error) return { data: null, error: error.message };
    return { data: (data as PorRecordSnapshot) ?? null, error: null };
  } catch (e) {
    return fail<PorRecordSnapshot | null>(null, e);
  }
}

/** Decide an executive ask (open → approved / deferred). Rationale required. */
export async function decideExecutiveAsk(
  askId: string,
  status: PorAskStatus,
  rationale: string,
): Promise<Result<boolean>> {
  try {
    if (!rationale.trim()) return { data: false, error: 'A rationale is required to decide an executive ask.' };
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('decide_executive_ask', {
      p_ask_id: askId,
      p_new_status: status,
      p_rationale: rationale.trim(),
    });
    if (error) return { data: false, error: error.message };
    const res = data as { success: boolean; error?: string };
    return { data: !!res?.success, error: res?.success ? null : res?.error ?? 'Failed to decide ask.' };
  } catch (e) {
    return fail<boolean>(false, e);
  }
}

// ─── Sections ───────────────────────────────────────────────────────────────

export async function updateSection(
  sectionId: string,
  patch: Partial<Pick<PorSection, 'title' | 'body' | 'status'>>,
): Promise<Result<PorSection | null>> {
  try {
    const supabase = getSupabaseClient();
    const actor = getTenantContext()?.userId ?? null;
    const { data, error } = await supabase
      .from('por_sections')
      .update({ ...patch, updated_at: new Date().toISOString(), updated_by: actor })
      .eq('id', sectionId)
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as PorSection, error: null };
  } catch (e) {
    return fail<PorSection | null>(null, e);
  }
}

// ─── Commitments ────────────────────────────────────────────────────────────

export async function createCommitment(
  porId: string,
  input: Partial<PorCommitment> & { title: string },
): Promise<Result<PorCommitment | null>> {
  try {
    const supabase = getSupabaseClient();
    const actor = getTenantContext()?.userId ?? null;
    const { data, error } = await supabase
      .from('por_commitments')
      .insert({ ...input, por_id: porId, created_by: actor })
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as PorCommitment, error: null };
  } catch (e) {
    return fail<PorCommitment | null>(null, e);
  }
}

export async function updateCommitment(
  commitmentId: string,
  patch: Partial<PorCommitment>,
): Promise<Result<PorCommitment | null>> {
  try {
    const supabase = getSupabaseClient();
    // The DB CHECK constraint enforces "blocked requires a blocker description",
    // but surface a friendly error before the round-trip.
    if (
      patch.status === 'blocked' &&
      (!patch.blocker_description || !patch.blocker_description.trim())
    ) {
      return { data: null, error: 'A blocked commitment needs a written blocker description.' };
    }
    const { data, error } = await supabase
      .from('por_commitments')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', commitmentId)
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as PorCommitment, error: null };
  } catch (e) {
    return fail<PorCommitment | null>(null, e);
  }
}

export async function deleteCommitment(commitmentId: string): Promise<Result<boolean>> {
  return simpleDelete('por_commitments', commitmentId);
}

// ─── KPIs ───────────────────────────────────────────────────────────────────

export async function updateKpi(
  kpiId: string,
  patch: Partial<PorKpi>,
): Promise<Result<PorKpi | null>> {
  try {
    const supabase = getSupabaseClient();
    const actor = getTenantContext()?.userId ?? null;
    const { data, error } = await supabase
      .from('por_kpis')
      .update({ ...patch, updated_at: new Date().toISOString(), updated_by: actor })
      .eq('id', kpiId)
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as PorKpi, error: null };
  } catch (e) {
    return fail<PorKpi | null>(null, e);
  }
}

// ─── Signal events ──────────────────────────────────────────────────────────

export async function createSignalEvent(
  porId: string,
  input: Partial<PorSignalEvent> & { condition: PorCondition; description: string; rationale: string },
): Promise<Result<PorSignalEvent | null>> {
  try {
    const supabase = getSupabaseClient();
    const actor = getTenantContext()?.userId ?? null;
    const { data, error } = await supabase
      .from('por_signal_events')
      .insert({ ...input, por_id: porId, created_by: actor })
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as PorSignalEvent, error: null };
  } catch (e) {
    return fail<PorSignalEvent | null>(null, e);
  }
}

export async function resolveSignalEvent(eventId: string, resolved: boolean): Promise<Result<boolean>> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('por_signal_events')
      .update({ resolved_at: resolved ? new Date().toISOString() : null })
      .eq('id', eventId);
    if (error) return { data: false, error: error.message };
    return { data: true, error: null };
  } catch (e) {
    return fail<boolean>(false, e);
  }
}

// ─── Risks ──────────────────────────────────────────────────────────────────

export async function createRisk(
  porId: string,
  input: Partial<PorRisk> & { description: string; mitigation: string },
): Promise<Result<PorRisk | null>> {
  try {
    const supabase = getSupabaseClient();
    const actor = getTenantContext()?.userId ?? null;
    const { data, error } = await supabase
      .from('por_risks')
      .insert({ ...input, por_id: porId, created_by: actor })
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as PorRisk, error: null };
  } catch (e) {
    return fail<PorRisk | null>(null, e);
  }
}

export async function updateRisk(riskId: string, patch: Partial<PorRisk>): Promise<Result<PorRisk | null>> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('por_risks')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', riskId)
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as PorRisk, error: null };
  } catch (e) {
    return fail<PorRisk | null>(null, e);
  }
}

export async function deleteRisk(riskId: string): Promise<Result<boolean>> {
  return simpleDelete('por_risks', riskId);
}

// ─── Executive asks ─────────────────────────────────────────────────────────
// Status changes go through decideExecutiveAsk (RPC). Everything else is a
// plain row write.

export async function createExecutiveAsk(
  porId: string,
  input: Partial<PorExecutiveAsk> & { description: string; rationale: string },
): Promise<Result<PorExecutiveAsk | null>> {
  try {
    const supabase = getSupabaseClient();
    const actor = getTenantContext()?.userId ?? null;
    const { data, error } = await supabase
      .from('por_executive_asks')
      .insert({ ...input, por_id: porId, created_by: actor })
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as PorExecutiveAsk, error: null };
  } catch (e) {
    return fail<PorExecutiveAsk | null>(null, e);
  }
}

export async function updateExecutiveAsk(
  askId: string,
  patch: Partial<Omit<PorExecutiveAsk, 'status' | 'decided_by' | 'decided_at'>>,
): Promise<Result<PorExecutiveAsk | null>> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('por_executive_asks')
      .update({ ...patch })
      .eq('id', askId)
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as PorExecutiveAsk, error: null };
  } catch (e) {
    return fail<PorExecutiveAsk | null>(null, e);
  }
}

export async function deleteExecutiveAsk(askId: string): Promise<Result<boolean>> {
  return simpleDelete('por_executive_asks', askId);
}

// ─── Proof points ───────────────────────────────────────────────────────────

export async function createProofPoint(
  porId: string,
  input: Partial<PorProofPoint> & { value_display: string; label: string },
): Promise<Result<PorProofPoint | null>> {
  try {
    const supabase = getSupabaseClient();
    const actor = getTenantContext()?.userId ?? null;
    const { data, error } = await supabase
      .from('por_proof_points')
      .insert({ ...input, por_id: porId, created_by: actor })
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as PorProofPoint, error: null };
  } catch (e) {
    return fail<PorProofPoint | null>(null, e);
  }
}

export async function updateProofPoint(
  proofPointId: string,
  patch: Partial<PorProofPoint>,
): Promise<Result<PorProofPoint | null>> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('por_proof_points')
      .update({ ...patch })
      .eq('id', proofPointId)
      .select('*')
      .single();
    if (error) return { data: null, error: error.message };
    return { data: data as PorProofPoint, error: null };
  } catch (e) {
    return fail<PorProofPoint | null>(null, e);
  }
}

export async function deleteProofPoint(proofPointId: string): Promise<Result<boolean>> {
  return simpleDelete('por_proof_points', proofPointId);
}

// ─── shared ─────────────────────────────────────────────────────────────────

async function simpleDelete(table: string, id: string): Promise<Result<boolean>> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase.from(table).delete().eq('id', id);
    if (error) return { data: false, error: error.message };
    return { data: true, error: null };
  } catch (e) {
    return fail<boolean>(false, e);
  }
}
